/**
 * Фабрика узлов X6: единая точка создания, обновления и удаления элементов с управлением жизненным циклом.
 * Использует NodeTypeRegistry и nodeConfig; все операции с узлами проходят через фабрику для согласованности.
 */
import {toRgbaString} from '../draw/ArchimateRendererUtil';
import {nodeTypeRegistry} from '../node-types/NodeTypeRegistry';
import {buildElementNodeConfig, getIconPath} from './nodeConfig';
import {normalizeType, getType} from './viewHelpers';
import {PATH_BASED_SHAPES} from './constants';

/**
 * @typedef {Object} CreateNodeOptions
 * @property {string} [id]
 * @property {number} [x]
 * @property {number} [y]
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [label]
 * @property {Object} [parent] — родительская ячейка X6 (для вложенности)
 * @property {Object} [viewElement] — view-элемент модели (bounds, style, label)
 * @property {string} [typeName] — отображаемое имя типа (для превью)
 * @property {string} [fillColor]
 * @property {string} [lineColor]
 * @property {string} [textAlign]
 */

/**
 * @typedef {Object} UpdateNodeOptions
 * @property {number} [x]
 * @property {number} [y]
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [label]
 * @property {string} [fillColor]
 * @property {string} [lineColor]
 * @property {string} [textAlign]
 * @property {Object} [viewElement]
 */

/**
 * Жизненный цикл: подписки вызываются в порядке регистрации.
 * before-хуки могут вернуть изменённые options; после них фабрика продолжает с этими options.
 */
export default class NodeFactory {
	/**
	 * @param {import('../ModelerX6').default} modeler — экземпляр ModelerX6 (pathMap, graph, viewHelpers)
	 */
	constructor(modeler) {
		this._modeler = modeler;
		/** @type {Array<(type: string, options: CreateNodeOptions) => CreateNodeOptions | void> */
		this._onBeforeCreate = [];
		/** @type {Array<(cell: import('@antv/x6').Node, type: string, options: CreateNodeOptions) => void> */
		this._onAfterCreate = [];
		/** @type {Array<(cell: import('@antv/x6').Node, options: UpdateNodeOptions) => UpdateNodeOptions | void> */
		this._onBeforeUpdate = [];
		/** @type {Array<(cell: import('@antv/x6').Node, options: UpdateNodeOptions) => void> */
		this._onAfterUpdate = [];
		/** @type {Array<(cell: import('@antv/x6').Node) => void> */
		this._onBeforeRemove = [];
		/** @type {Array<(cellId: string) => void> */
		this._onAfterRemove = [];
	}

	/**
	 * Построить конфиг узла без добавления на граф.
	 * @param {string} type — тип элемента (ApplicationComponent, Group, …)
	 * @param {CreateNodeOptions} [options]
	 * @returns {Object} nodeConfig для graph.addNode() / graph.createNode()
	 */
	create(type, options = {}) {
		const normalizedType = normalizeType(type);
		const nodeType = nodeTypeRegistry.get(normalizedType);
		if (!nodeType) {
			throw new Error(`Unsupported element type: ${normalizedType}`);
		}
		let opts = {...options};
		for (const fn of this._onBeforeCreate) {
			const next = fn(normalizedType, opts);
			if (next != null) opts = next;
		}
		const config = buildElementNodeConfig(this._modeler, type, opts);
		return config;
	}

	/**
	 * Создать узел и добавить его на граф.
	 * @param {string} type
	 * @param {CreateNodeOptions} [options]
	 * @returns {import('@antv/x6').Node}
	 */
	createAndAdd(type, options = {}) {
		const config = this.create(type, options);
		const graph = this._modeler.getGraph();
		if (!graph) {
			throw new Error('Graph is not initialized');
		}
		// X6 не гарантирует замену при повторном id — удаляем старую ячейку, чтобы не было двух узлов (призраков)
		const id = config.id ?? options.id;
		// В граф не передаём silent: иначе view не обновляется и ячейки не рендерятся (X6).
		const addOpts = {};
		if (id) {
			const existing = graph.getCellById?.(id) ?? graph.model?.getCellById?.(id);
			if (existing) graph.removeCell?.(existing, addOpts);
		}
		const cell = graph.addNode(config, addOpts);
		// Вложенность задаётся в renderView вторым проходом (parent.addChild), когда все узлы уже на графе
		const normalizedType = normalizeType(type);
		for (const fn of this._onAfterCreate) {
			fn(cell, normalizedType, options);
		}
		return cell;
	}

	/**
	 * Создать узел без добавления на граф (для отображения в палитре Stencil и для превью при драге).
	 * Размеры задаёт вызывающий код; при начале драга Stencil использует getDragNode, где узел ресайзится и path обновляется.
	 * @param {string} type
	 * @param {{ width?: number, height?: number, label?: string, typeName?: string }} [options]
	 * @returns {import('@antv/x6').Node}
	 */
	createPreview(type, options = {}) {
		const config = this.create(type, options);
		const graph = this._modeler.getGraph();
		if (!graph) {
			throw new Error('Graph is not initialized');
		}
		return graph.createNode(config);
	}

	/**
	 * Обновить узел: пересобрать attrs (и при необходимости path body/icon) по текущему состоянию ячейки и/или options.
	 * @param {import('@antv/x6').Node} cell
	 * @param {UpdateNodeOptions} [options] — переданные значения перекрывают данные из cell/viewElement
	 */
	update(cell, options = {}) {
		if (!cell || typeof cell.isNode !== 'function' || !cell.isNode()) return;
		const shapeId = typeof cell.getShape === 'function' ? cell.getShape() : cell.shape;
		const viewElement = this._modeler._findViewElementById?.(cell.id);
		const style = viewElement?.style;
		const hasStyleFromView =
			style && (style.fillColor ?? style.lineColor ?? style.textAlign != null);
		const isJunctionShape =
			shapeId === 'archimate:and-junction' || shapeId === 'archimate:or-junction';
		if (!PATH_BASED_SHAPES.includes(shapeId) && !isJunctionShape) {
			// rect-элементы: обновляем только при наличии стиля или явных options (внешний вид из Properties)
			if (
				!hasStyleFromView &&
				options.fillColor === undefined &&
				options.lineColor === undefined &&
				options.textAlign === undefined &&
				options.label === undefined
			) {
				return;
			}
		}
		const elemType = viewElement
			? getType(this._modeler, viewElement)
			: normalizeType(cell.getData?.()?.elementType ?? '');
		const nodeType = nodeTypeRegistry.get(elemType);
		if (!nodeType || typeof nodeType.buildNodeConfig !== 'function') return;

		let opts = {...options};
		const pos = cell.getPosition?.() ?? {x: 0, y: 0};
		const size = cell.getSize?.() ?? {};
		const attrs = cell.getAttrs?.() ?? {};
		opts.id = opts.id ?? cell.id;
		opts.x = opts.x ?? pos.x;
		opts.y = opts.y ?? pos.y;
		opts.width = opts.width ?? size.width;
		opts.height = opts.height ?? size.height;
		opts.label = opts.label ?? attrs?.label?.text ?? viewElement?.label ?? '';
		const fillFromStyle = style && (style.fillColor ?? style.get?.('fillColor'));
		const lineFromStyle = style && (style.lineColor ?? style.get?.('lineColor'));
		// Для junction не подставляем цвет из текущих attrs — тип задаёт заливку (And: чёрный, Or: без заливки)
		const fillFallback = isJunctionShape ? undefined : attrs?.body?.fill;
		const strokeFallback = isJunctionShape ? undefined : attrs?.body?.stroke;
		opts.fillColor =
			opts.fillColor ?? (fillFromStyle && toRgbaString(fillFromStyle)) ?? fillFallback;
		opts.lineColor =
			opts.lineColor ?? (lineFromStyle && toRgbaString(lineFromStyle)) ?? strokeFallback;
		opts.textAlign = opts.textAlign ?? style?.textAlign ?? viewElement?.textAlign ?? null;
		opts.lineWidth = opts.lineWidth ?? style?.lineWidth ?? undefined;

		for (const fn of this._onBeforeUpdate) {
			const next = fn(cell, opts);
			if (next != null) opts = next;
		}

		const config = nodeType.buildNodeConfig(this._modeler._pathMap, opts);
		if (!config.attrs || typeof cell.setAttrs !== 'function') return;
		cell.setAttrs(config.attrs);
		if (config.attrs.body?.d != null && typeof cell.setAttrByPath === 'function') {
			cell.setAttrByPath('body/d', config.attrs.body.d);
		}
		// Junction: принудительно задаём body/fill (X6 может не обновлять при merge)
		if (
			isJunctionShape &&
			config.attrs.body?.fill !== undefined &&
			typeof cell.setAttrByPath === 'function'
		) {
			cell.setAttrByPath('body/fill', config.attrs.body.fill);
		}

		for (const fn of this._onAfterUpdate) {
			fn(cell, opts);
		}
	}

	/**
	 * Удалить узел с графа. Модель (viewElements) не меняется — вызывающий код должен синхронизировать при необходимости.
	 * @param {import('@antv/x6').Node | string} cellOrId — ячейка или id
	 */
	remove(cellOrId) {
		const graph = this._modeler.getGraph();
		if (!graph) return;
		const cell =
			typeof cellOrId === 'string'
				? (graph.getCellById?.(cellOrId) ?? graph.model?.getCellById?.(cellOrId))
				: cellOrId;
		if (!cell || (typeof cell.isNode === 'function' && !cell.isNode())) return;

		for (const fn of this._onBeforeRemove) {
			fn(cell);
		}
		const id = cell.id;
		this._modeler._releaseCellChrome?.(cell);
		graph.removeCell(cell);
		for (const fn of this._onAfterRemove) {
			fn(id);
		}
	}

	// --- Lifecycle hooks (подписки) ---

	onBeforeCreate(fn) {
		this._onBeforeCreate.push(fn);
		return () => {
			const i = this._onBeforeCreate.indexOf(fn);
			if (i >= 0) this._onBeforeCreate.splice(i, 1);
		};
	}

	onAfterCreate(fn) {
		this._onAfterCreate.push(fn);
		return () => {
			const i = this._onAfterCreate.indexOf(fn);
			if (i >= 0) this._onAfterCreate.splice(i, 1);
		};
	}

	onBeforeUpdate(fn) {
		this._onBeforeUpdate.push(fn);
		return () => {
			const i = this._onBeforeUpdate.indexOf(fn);
			if (i >= 0) this._onBeforeUpdate.splice(i, 1);
		};
	}

	onAfterUpdate(fn) {
		this._onAfterUpdate.push(fn);
		return () => {
			const i = this._onAfterUpdate.indexOf(fn);
			if (i >= 0) this._onAfterUpdate.splice(i, 1);
		};
	}

	onBeforeRemove(fn) {
		this._onBeforeRemove.push(fn);
		return () => {
			const i = this._onBeforeRemove.indexOf(fn);
			if (i >= 0) this._onBeforeRemove.splice(i, 1);
		};
	}

	onAfterRemove(fn) {
		this._onAfterRemove.push(fn);
		return () => {
			const i = this._onAfterRemove.indexOf(fn);
			if (i >= 0) this._onAfterRemove.splice(i, 1);
		};
	}

	/**
	 * Обновить иконку узла по размеру (для rect-элементов при cell:change:size).
	 * @param {import('@antv/x6').Node} cell
	 */
	refreshIcon(cell) {
		const iconRef = cell.getData?.()?.iconRef;
		if (!iconRef) return;
		const size = cell.getSize?.();
		if (!size || size.width <= 0 || size.height <= 0) return;
		const iconBoxW = Math.round(0.22 * size.width);
		const iconBoxH = Math.round(0.45 * size.height);
		const newPath = getIconPath(this._modeler, iconRef, iconBoxW, iconBoxH);
		if (typeof cell.setAttrByPath === 'function') {
			cell.setAttrByPath('icon/d', newPath);
		}
	}
}
